import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  bodyErrors,
  bodyTargetErrors,
  guardErrors,
  idParams,
  targetErrors,
} from '../common';
import { financesSummarySchema, goalSchema, walletSchema } from '../entities';
import {
  amountInput,
  monthInput,
  nameInput,
  positiveAmountInput,
} from '../inputs';

const c = initContract();

export const walletBody = z.object({
  name: nameInput,
  balance: amountInput,
});

export const goalBody = z.object({
  name: nameInput,
  targetAmount: positiveAmountInput,
  targetDate: monthInput,
});

/**
 * The body *after* parsing — normalised amounts, `targetDate` widened from the
 * form's "YYYY-MM" to a real date. This is what a handler receives, so services
 * take these rather than re-declaring the same fields by hand and drifting.
 * The browser sends `z.input` of the same schema, which ts-rest infers for it.
 */
export type WalletBody = z.infer<typeof walletBody>;
export type GoalBody = z.infer<typeof goalBody>;

export const financesContract = c.router(
  {
    /** The date-priority allocation waterfall stays on the server. */
    overview: {
      method: 'GET',
      path: '/overview',
      responses: { 200: financesSummarySchema, ...guardErrors },
    },

    listWallets: {
      method: 'GET',
      path: '/wallets',
      responses: { 200: z.array(walletSchema), ...guardErrors },
    },

    createWallet: {
      method: 'POST',
      path: '/wallets',
      body: walletBody,
      responses: { 201: walletSchema, ...bodyErrors },
    },

    updateWallet: {
      method: 'PATCH',
      path: '/wallets/:id',
      pathParams: idParams,
      body: walletBody,
      responses: { 200: walletSchema, ...bodyTargetErrors },
    },

    deleteWallet: {
      method: 'DELETE',
      path: '/wallets/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
    },

    listGoals: {
      method: 'GET',
      path: '/goals',
      responses: { 200: z.array(goalSchema), ...guardErrors },
    },

    createGoal: {
      method: 'POST',
      path: '/goals',
      body: goalBody,
      responses: { 201: goalSchema, ...bodyErrors },
    },

    updateGoal: {
      method: 'PATCH',
      path: '/goals/:id',
      pathParams: idParams,
      body: goalBody,
      responses: { 200: goalSchema, ...bodyTargetErrors },
    },

    deleteGoal: {
      method: 'DELETE',
      path: '/goals/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
    },
  },
  { pathPrefix: '/finances' },
);
